import { apiClient } from './api'

export type UserRole = 'farmer' | 'buyer' | 'transporter' | 'supplier' | 'event_organizer' | 'admin'

export interface FarmerProfile {
  id: number
  user_id: number
  farm_size?: number | null
  crop_types?: string
  is_subscribed: boolean
  subscribed_plan?: string | null
  subscription_expiry?: string | null
  marketplace_active: boolean
  marketplace_expiry?: string | null
}

export interface BuyerProfile {
  id: number
  user_id: number
  company_name?: string | null
  buying_preferences?: string
  is_verified_buyer: boolean
}

export interface TransporterProfile {
  id: number
  user_id: number
  vehicle_type: string
  vehicle_capacity: number
  service_areas?: string
  is_available: boolean
  rating: number
}

export interface SupplierProfile {
  id: number
  user_id: number
  business_name?: string | null
  supplier_categories?: string
  is_subscribed: boolean
  subscribed_plan?: string | null
  subscription_expiry?: string | null
}

export interface AdminProfile {
  id: number
  user_id: number
  admin_level: string
  permissions: string
}

export interface AuthUser {
  id: number
  role: UserRole
  first_name: string
  last_name: string
  email?: string | null
  phone_number: string
  whatsapp_number?: string | null
  location: string
  registration_channel: string
  registration_date?: string
  last_activity?: string
  is_active?: boolean
  email_verified?: boolean
  phone_verified?: boolean
  created_at?: string
  updated_at?: string
  subscribed_plan?: string | null
  subscription_expiry?: string | null
  farmer_profile?: FarmerProfile | null
  buyer_profile?: BuyerProfile | null
  transporter_profile?: TransporterProfile | null
  supplier_profile?: SupplierProfile | null
  admin_profile?: AdminProfile | null
}

export interface LoginRequest {
  phone_number?: string
  email?: string
  password?: string
  channel: string
}

export interface OTPRequestResponse {
  message: string
}

export interface OTPVerifyResponse extends AuthResponse {}


export interface RegisterRequest {
  role: UserRole
  first_name: string
  last_name: string
  email?: string
  phone_number: string
  location: string
  password?: string
  registration_channel: string
  farm_size?: number
  crop_types?: string[]
  company_name?: string
  buying_preferences?: string[]
  vehicle_type?: string
  vehicle_capacity?: number
  service_areas?: string[]
  business_name?: string
  supplier_categories?: string[]
  admin_level?: string
}

export interface AuthResponse {
  success?: boolean
  message?: string
  user: AuthUser
  access_token: string
  refresh_token: string
  expires_at?: string
}

export interface CurrentUserResponse {
  user: AuthUser
}

export interface UpdateProfileRequest {
  first_name?: string
  last_name?: string
  email?: string
  phone_number?: string
  location?: string
  farm_size?: number
  crop_types?: string[]
  company_name?: string
  buying_preferences?: string[]
  vehicle_type?: string
  vehicle_capacity?: number
  service_areas?: string[]
  is_available?: boolean
  business_name?: string
  supplier_categories?: string[]
}

export interface RefreshTokenRequest {
  refresh_token: string
}

export class AuthService {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>('/api/auth/login', credentials)
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>('/api/auth/register', userData)
  }

  async refreshToken(refreshToken: string): Promise<{ access_token: string; refresh_token?: string; expires_at?: string }> {
    return apiClient.post<{ access_token: string; refresh_token?: string; expires_at?: string }>('/api/auth/refresh', {
      refresh_token: refreshToken,
    })
  }

  async logout(): Promise<void> {
    await apiClient.post('/api/auth/logout')
  }

  async verifyToken(token: string): Promise<{ valid: boolean }> {
    return apiClient.post<{ valid: boolean }>('/api/auth/verify', { token })
  }

  async requestOTP(phoneNumber: string): Promise<OTPRequestResponse> {
    return apiClient.post<OTPRequestResponse>('/api/auth/whatsapp-otp/request', {
      phone_number: phoneNumber,
    })
  }

  async verifyOTP(phoneNumber: string, code: string): Promise<OTPVerifyResponse> {
    return apiClient.post<OTPVerifyResponse>('/api/auth/whatsapp-otp/verify', {
      phone_number: phoneNumber,
      code,
    })
  }

  async getCurrentUser(): Promise<CurrentUserResponse> {
    return apiClient.get<CurrentUserResponse>('/api/users/me')
  }

  async updateCurrentUser(profile: UpdateProfileRequest): Promise<CurrentUserResponse & { message: string }> {
    return apiClient.put<CurrentUserResponse & { message: string }>('/api/users/me', profile)
  }
}

export const authService = new AuthService()
